// Helpers and utilities
import { Link } from "react-router-dom";
import ReactGA from "react-ga4";

// Types
import { RecipeFieldTypes } from "src/types/contentfulTypes";

// Components
import Image from "src/components/atoms/image/Image";
import RecipePin from "src/components/atoms/recipePin/RecipePin";
import ImagePlaceholder from "src/components/atoms/imagePlaceholder/ImagePlaceholder";
import RecipeIngredients from "src/components/molecules/recipeIngredients/RecipeIngredients";

// Styles
import "./RecipeInfo.scss";

interface RecipeInfoTypes
  extends Omit<
    RecipeFieldTypes,
    "id" | "name" | "image" | "thumbnailImage" | "steps"
  > {}

const RecipeInfo = ({
  heroImage,
  ingredients,
  serves,
  cooking_time,
  tags,
  slug,
}: RecipeInfoTypes) => {
  const cuisineTag = tags && tags.find((tag) => tag.type === "cuisine");
  const onCuisineClick = () => {
    if (!cuisineTag?.slug) {
      return;
    }

    ReactGA.event({
      category: "Recipe",
      action: "Cuisine tag clicked",
      label: cuisineTag.slug,
    });
  };

  return (
    <div className="grid__wrapper">
      <div className="grid">
        <div className="grid__half">
          {heroImage?.url ? (
            <Image imageObj={heroImage} imageClass="recipe-info__image" />
          ) : (
            <ImagePlaceholder />
          )}
        </div>
        <div className="grid__half grid__half--column">
          <div className="recipe-info shadow--light">
            {cuisineTag && (
              <>
                <h3 className="visually-hidden">Cuisine</h3>
                <Link
                  className="recipe-info__block recipe-info__block--cuisine"
                  to="/"
                  onClick={onCuisineClick}
                  // Pass the cuisine through navigation state so it is the one
                  // filter that intentionally survives the trip back home.
                  state={{ filteredTags: [cuisineTag] }}
                >
                  {cuisineTag.title}
                </Link>
              </>
            )}
            <div className="recipe-info__block recipe-info__block--split">
              <div>
                <h3 className="visually-hidden">Servings</h3>
                <span className="recipe-info__block-number">{serves}</span>
                <span className="recipe-info__block-label">
                  {serves && serves > 1 ? "servings" : "servings"}
                </span>
              </div>
              <div>
                <h3 className="visually-hidden">Cooking time</h3>
                <span className="recipe-info__block-number">
                  {cooking_time}
                </span>
                <span className="recipe-info__block-label">mins</span>
              </div>
              <RecipePin slug={slug} />
            </div>
          </div>

          <RecipeIngredients ingredients={ingredients} />
        </div>
      </div>
    </div>
  );
};

export default RecipeInfo;
